// Central BPA customer identity. Public base URL is not a secret.
// Never put PocketBase superuser credentials in a Greta/Netlify build.
const BASE='https://bpas-applications-hub-google-sheets-wint.pockethost.io';
const STORE='bpa-customer-session-v1';
export const BPA_SESSION_STORAGE_KEY=STORE;
export const APP_ID=(import.meta.env.VITE_BPA_APP_ID||'').trim();
export const configured=()=>Boolean(APP_ID);

function load() {
  try {return JSON.parse(localStorage.getItem(STORE)||'null');} catch {return null;}
}
export const getStoredSession=()=>load();
export const clearSession=()=>{try{localStorage.removeItem(STORE);}catch{/* no persistence */}};
function setSession(value){localStorage.setItem(STORE,JSON.stringify(value));return value;}
async function request(path,{method='GET',body,token}={}){
  let response;
  try{
    response=await fetch(BASE+path,{method,headers:{
      ...(body?{'Content-Type':'application/json'}:{}),
      ...(token?{Authorization:token}:{})
    },...(body?{body:JSON.stringify(body)}:{})});
  }catch{throw new Error('Cannot reach BPA customer authentication. Check your connection and try again.');}
  const result=await response.json().catch(()=>({}));
  if(!response.ok){
    const friendly=result.message;
    const error=new Error(response.status===401?'Your session has expired. Sign in again.':
      response.status===403?(friendly||'This customer does not have access to this application.'):
      response.status===400 && path.includes('auth-with-password')?'The email or password is incorrect.':
      friendly||'BPA customer account request failed ('+response.status+').');
    error.status=response.status;
    // Show safe, actionable PocketBase field-level validation messages.
    // Do not surface tokens or original request values in user-facing text.
    if(result.data && typeof result.data==='object'){
      const issues=Object.entries(result.data).filter(([name])=>/^[a-z_]{1,45}$/.test(name))
        .map(([name,item])=>name+': '+String(item?.message||'validation failed').slice(0,150));
      if(issues.length && !path.includes('auth-with-password'))error.message+=' ('+issues.join('; ').slice(0,320)+')';
    }
    throw error;
  }
  return result;
}
export async function authenticate(email,password){
  if(!APP_ID)throw new Error('BPA application ID is not configured. The administrator must set VITE_BPA_APP_ID.');
  const auth=await request('/api/collections/bpa_users/auth-with-password',
    {method:'POST',body:{identity:String(email).trim(),password}});
  const access=await verifyAccess(auth.token);
  if(access?.customer?.id && access.customer.id!==auth.record.id)throw new Error('BPA customer identity does not match the application entitlement.');
  if(auth.record.account_status!=='active')throw new Error('This BPA customer account is not active.');
  return setSession({token:auth.token,record:{id:auth.record.id,email:auth.record.email,name:auth.record.display_name},access});
}
async function verifyAccess(token){
  const result=await request('/api/bpa-customer/v1/access?app_id='+encodeURIComponent(APP_ID),{token});
  if(result?.allowed===false || result?.active===false || !/^[a-z0-9]{15}$/.test(String(result?.entitlementId||''))) {
    const error=new Error('A valid active Expense Manager Pro entitlement is required.');
    error.status=403;throw error;
  }
  return result;
}
export async function restoreSession(){
  const previous=load();
  if(!previous?.token||!APP_ID)return null;
  try{
    const auth=await request('/api/collections/bpa_users/auth-refresh',{method:'POST',token:previous.token});
    if(auth.record.account_status!=='active')throw new Error('BPA account inactive.');
    const access=await verifyAccess(auth.token);
    if(access?.customer?.id && access.customer.id!==auth.record.id)throw new Error('BPA customer identity does not match the application entitlement.');
    return setSession({token:auth.token,record:{id:auth.record.id,email:auth.record.email,name:auth.record.display_name},access});
  }catch(err){clearSession();if(err?.status===403)throw err;return null;}
}
export async function requestPasswordReset(email){
  const value=String(email||'').trim();
  if(!value)throw new Error('Enter your registered BPA email first.');
  await request('/api/collections/bpa_users/request-password-reset',{method:'POST',body:{email:value}});
  return true;
}
export async function confirmPasswordReset(token,password,confirmation){
  if(!token||!password||password!==confirmation)throw new Error('The reset link or passwords are invalid.');
  if(password.length<8)throw new Error('Choose a password with at least 8 characters.');
  await request('/api/collections/bpa_users/confirm-password-reset',{method:'POST',
    body:{token,password,passwordConfirm:confirmation}});
  clearSession();
  return true;
}

// The deployed schema deliberately supports customer-owned connection records.
// This normal PocketBase API uses exactly the same bpa_users bearer token and
// ownership/entitlement rules as the custom hook. No admin credentials are used.
const CONNECTION_COLLECTION='/api/collections/bpa_connections/records';
const CONNECTION_SLOT='primary_google_sheet';
async function entitledCustomer(token){
  const access=await verifyAccess(token);
  const userId=getStoredSession()?.record?.id;
  if(!/^[a-z0-9]{15}$/.test(String(userId||'')) ||
     !/^[a-z0-9]{15}$/.test(String(access?.entitlementId||''))){
    throw new Error('BPA customer or purchased app entitlement could not be verified.');
  }
  if(access?.customer?.id && access.customer.id!==userId)throw new Error('Customer identity does not match the purchased app.');
  return {userId,entitlementId:access.entitlementId};
}
async function readDirectConnection(token,entitlementId){
  const filter='entitlement = "'+entitlementId+'" && connection_key = "'+CONNECTION_SLOT+'"';
  const query=new URLSearchParams({filter,perPage:'1',fields:'id,resource_id,resource_label,schema_version,updated,created_by,entitlement'});
  const result=await request(CONNECTION_COLLECTION+'?'+query.toString(),{token});
  const row=Array.isArray(result.items)?result.items[0]:null;
  return row||null;
}
function normalisedConnection(row){
  if(!row)return null;
  return {id:row.id,spreadsheetId:row.resource_id,label:row.resource_label,
    schemaVersion:row.schema_version,updated:row.updated};
}
async function saveDirectConnection(token,spreadsheetId,label){
  const {userId,entitlementId}=await entitledCustomer(token);
  const existing=await readDirectConnection(token,entitlementId);
  if(existing){
    // Immutable relation fields are intentionally omitted during updates.
    const updated=await request(CONNECTION_COLLECTION+'/'+encodeURIComponent(existing.id),{
      method:'PATCH',token,body:{resource_id:spreadsheetId,resource_label:label,schema_version:'2'}});
    if(updated.resource_id!==spreadsheetId)throw new Error('Saved Sheet ID did not match the requested ID.');
    return normalisedConnection(updated);
  }
  const payload={entitlement:entitlementId,created_by:userId,connection_key:CONNECTION_SLOT,
    provider:'google_sheets',resource_id:spreadsheetId,resource_label:label,schema_version:'2'};
  let created;
  try{created=await request(CONNECTION_COLLECTION,{method:'POST',token,body:payload});}
  catch(error){
    // A simultaneous connect may have created the unique entitlement/slot.
    if(error.status!==400)throw error;
    const raced=await readDirectConnection(token,entitlementId);
    if(!raced)throw error;
    created=await request(CONNECTION_COLLECTION+'/'+encodeURIComponent(raced.id),{
      method:'PATCH',token,body:{resource_id:spreadsheetId,resource_label:label,schema_version:'2'}});
  }
  if(created.resource_id!==spreadsheetId)throw new Error('Saved Sheet ID did not match the requested ID.');
  return normalisedConnection(created);
}

/**
 * Save only the Sheet reference, never Google access/refresh tokens here.
 * The central PocketBase route checks that this token owns an active entitlement for APP_ID.
 */
export async function saveSheetConnection(token,spreadsheetId,label='BPA Expense Manager Pro Google Sheet'){
  if(!token||!APP_ID)throw new Error('Sign in to your BPA customer account first.');
  if(!/^[A-Za-z0-9_-]{20,120}$/.test(String(spreadsheetId||'')))throw new Error('Invalid Google Sheet ID.');
  try{
    const result=await request('/api/bpa-customer/v1/connections',{method:'POST',token,
      body:{app_id:APP_ID,spreadsheetId,label,schemaVersion:'2'}});
    if(result.saved && result.connection?.spreadsheetId===spreadsheetId && result.connection?.id)return result.connection;
    throw new Error('BPA customer hook did not confirm the saved spreadsheet ID.');
  }catch(error){
    // The imported v2 collection permits authenticated customer-owned writes.
    // Recover from an outdated or unavailable hook, not an authentication or
    // licence denial. Each direct write is still checked by PocketBase rules.
    if(error.status===401||error.status===403)throw error;
    try{return await saveDirectConnection(token,spreadsheetId,label);}
    catch(directError){
      throw new Error('Central Sheet save failed. Fallback also failed: '+directError.message);
    }
  }
}
export async function getSheetConnection(token){
  if(!token||!APP_ID)return null;
  try{
    const result=await request('/api/bpa-customer/v1/connections?app_id='+encodeURIComponent(APP_ID),{token});
    if(result?.connection?.spreadsheetId)return result.connection;
  }catch(error){
    if(error.status===401||error.status===403)throw error;
  }
  // A legacy v2 hook may return null even when a valid direct record exists.
  const {entitlementId}=await entitledCustomer(token);
  return normalisedConnection(await readDirectConnection(token,entitlementId));
}